import axios from 'axios'
import { selectors as commonSelectors } from 'mage-common'
import { memoizeWithTokenHelper } from 'mage-deps/@mpp/token-helper'
import * as R from 'ramda'
import { createSelector } from 'reselect'
import uuid from 'uuid/v4'

const coreState = R.propOr({}, 'core')

const clientId = R.pipe(
  coreState,
  R.prop('clientId')
)

const title = R.pipe(
  coreState,
  R.prop('title')
)

const slug = R.pipe(
  coreState,
  R.propOr('', 'slug')
)

const backendUrl = R.pipe(
  slug,
  R.concat('/api/')
)

const useBasename = R.pipe(
  coreState,
  R.prop('useBasename')
)

const getBasename = createSelector(
  slug,
  useBasename,
  (slug, useBasename) => useBasename ? `/${slug}` : ''
)

const keycloakRealm = R.pipe(
  coreState,
  R.prop('realm')
)

const isKlarnaRealm = R.pipe(
  keycloakRealm,
  R.equals('klarna')
)

const isMerchantsRealm = R.pipe(
  keycloakRealm,
  R.equals('merchants')
)

const isPartner = R.pipe(
  coreState,
  R.propEq('isPartner', true)
)

const showBetaLabel = R.pipe(
  coreState,
  R.prop('showBetaLabel')
)

const sessionId = R.pipe(
  coreState,
  R.prop('sessionId')
)

const showAppsMenu = R.pipe(
  coreState,
  R.prop('showAppsMenu')
)

const authenticated = R.pipe(
  coreState,
  R.prop('authenticated')
)

const isAuthenticated = createSelector(
  authenticated,
  R.equals(true)
)

const getSelectedMid = createSelector(
  coreState,
  R.prop('selectedMid')
)

const getAccessToken = R.pipe(
  coreState,
  R.prop('accessToken')
)

const getAccessTokenIssuedAt = R.pipe(
  coreState,
  R.prop('accessTokenIssuedAt')
)

/**
 * !!! DEPRECATED !!!
 * Use the token-helper to get this data, as this selector will be removed soon(-ish)
 * https://jira.int.klarna.net/jira/browse/MPP-5080
 */
const getMerchantIds = createSelector(
  coreState,
  R.propOr([], 'mids')
)

const getClientRoles = createSelector(
  clientId,
  getSelectedMid,
  (clientId, mid) => memoizeWithTokenHelper(
    tokenHelper => tokenHelper.getPermissionsForMid(mid, clientId)
  )
)

const canUpdateClientPermissions = createSelector(
  getSelectedMid,
  isKlarnaRealm,
  (selectedMid, isKlarnaRealm) => memoizeWithTokenHelper(
    tokenHelper => {
      return clientId => commonSelectors.tokenHelper.isAdmin(tokenHelper) || tokenHelper.hasFullPermissionForMid(selectedMid, clientId, 'merchant-full') || isKlarnaRealm
    }
  )
)

const isUpdateProfileLoading = R.pipe(
  coreState,
  R.prop('isUpdateProfileLoading')
)

const isUpdateProfileFailed = R.pipe(
  coreState,
  R.prop('isUpdateProfileFailed')
)

const getDesignVersion = R.pipe(
  coreState,
  R.prop('designVersion')
)

const getRequester = R.pipe(
  coreState,
  R.prop('requester')
)

const isRequesterTotpOn = R.pipe(
  getRequester,
  R.prop('totp')
)

const isRequesterKred = R.pipe(
  getRequester,
  R.prop('is_kred')
)

const getRequesterAttributes = R.pipe(
  getRequester,
  R.prop('attributes')
)

const clientFactory = createSelector(
  getAccessToken,
  accessToken => R.memoizeWith(
    R.identity,
    baseURL => {
      const client = axios.create({
        baseURL,
        timeout: 60000,
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      })
      client.interceptors.request.use(config => {
        config.headers.common['Klarna-Correlation-Id'] = uuid()
        return config
      })
      return client
    }
  )
)

const createClient = createSelector(
  clientFactory,
  backendUrl,
  (factory, baseUrl) => factory(baseUrl)
)

export {
  coreState,
  clientId,
  title,
  slug,
  backendUrl,
  useBasename,
  getBasename,
  keycloakRealm,
  isKlarnaRealm,
  isMerchantsRealm,
  isPartner,
  showBetaLabel,
  sessionId,
  showAppsMenu,
  authenticated,
  isAuthenticated,
  getSelectedMid,
  getAccessToken,
  getAccessTokenIssuedAt,
  getMerchantIds,
  getClientRoles,
  canUpdateClientPermissions,
  isUpdateProfileLoading,
  isUpdateProfileFailed,
  getDesignVersion,
  getRequester,
  isRequesterTotpOn,
  isRequesterKred,
  getRequesterAttributes,
  clientFactory,
  createClient
}
